import { useEffect, useState } from "react";
import { aircraft as aircraftApi } from "../api/client";
import type { AircraftDetail, AircraftPhoto, FlightRoute, Outlook, ProjectionPoint, TrackPoint } from "./types";

export type Selection = {
  detail: AircraftDetail | null;
  photo: AircraftPhoto | null;
  route: FlightRoute | null;
  outlook: Outlook[];
  track: TrackPoint[];
  projection: ProjectionPoint[];
  /** False once the first projection response has come back, so the sheet can say "no forecast". */
  loading: boolean;
};

/**
 * The track and projection for whichever aircraft is selected.
 *
 * The projection is refetched on every live update, because it is computed from the aircraft's
 * *current* position — a five-minute forecast anchored to where it was thirty seconds ago is
 * wrong by a couple of kilometres at the near end, which is exactly the end that matters. The
 * trail is refetched far less often; new points only appear every fifteen seconds anyway.
 */
export function useSelection(hex: string | null, liveGeneration: number): Selection {
  const [detail, setDetail] = useState<AircraftDetail | null>(null);
  const [photo, setPhoto] = useState<AircraftPhoto | null>(null);
  const [route, setRoute] = useState<FlightRoute | null>(null);
  const [outlook, setOutlook] = useState<Outlook[]>([]);
  const [track, setTrack] = useState<TrackPoint[]>([]);
  const [projection, setProjection] = useState<ProjectionPoint[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!hex) {
      setDetail(null);
      setPhoto(null);
      setRoute(null);
      setTrack([]);
      setProjection([]);
      return;
    }
    let cancelled = false;
    setLoading(true);
    // Fetched even though the live list already carries most of these fields: the detail route
    // falls back to the learned type dictionary for the model name, which is what keeps a model
    // on screen while the feed has failed over to adsb.lol (PLAN D1).
    aircraftApi
      .detail(hex)
      .then((d) => !cancelled && setDetail(d))
      .catch(() => !cancelled && setDetail(null));
    aircraftApi
      .track(hex)
      .then((r) => !cancelled && setTrack(r.points))
      .catch(() => !cancelled && setTrack([]));
    // Most aircraft have no photo, and the request may go all the way to Planespotters on a
    // cache miss — so it is deliberately independent of everything else the sheet needs. The
    // sheet renders complete without it.
    setPhoto(null);
    setRoute(null);
    aircraftApi
      .route(hex)
      .then((r) => !cancelled && setRoute(r.route))
      .catch(() => !cancelled && setRoute(null));
    aircraftApi
      .photo(hex)
      .then((r) => !cancelled && setPhoto(r.photo))
      .catch(() => !cancelled && setPhoto(null));
    return () => {
      cancelled = true;
    };
  }, [hex]);

  useEffect(() => {
    if (!hex) {
      setOutlook([]);
      return;
    }
    let cancelled = false;
    // Refreshed with the projection: both are computed from the aircraft's current position.
    aircraftApi
      .outlook(hex)
      .then((r) => !cancelled && setOutlook(r.outlook))
      .catch(() => !cancelled && setOutlook([]));
    aircraftApi
      .projection(hex)
      .then((r) => {
        if (cancelled) return;
        setProjection(r.points);
        setLoading(false);
      })
      .catch(() => {
        if (cancelled) return;
        // A 404 here means the aircraft went out of coverage between the tick and this request.
        // The selection is cleared by the page; an empty forecast is the right interim state.
        setProjection([]);
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [hex, liveGeneration]);

  return { detail, photo, route, outlook, track, projection, loading };
}
